import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { Link } from 'react-router'

import {
  fetchHospitalProcedures,
  hospitalProceduresKey,
} from '../../api/procedures'
import { readPrice } from '../../lib/money'
import { PriceCaveat } from '../PriceCaveat'
import { Icon } from '../Icon'

/**
 * Pricing tab (M12-T03, ADR-0028). Procedure prices (existing M8 data). The old
 * per-hospital drug-stock search is retired — medicine availability is answered
 * by the pharmacies on request, so this links out to `/medicines` (T06). No
 * bed/ward price table: bed prices live only behind a bed-type choice (Beds tab
 * → `/book/:slug?type=…`, ADR-0027).
 */
export function PricingTab({ slug }: { slug: string }): ReactNode {
  const query = useQuery({
    queryKey: hospitalProceduresKey(slug),
    queryFn: ({ signal }) => fetchHospitalProcedures(slug, signal),
  })
  const groups = query.data ?? []

  return (
    <div className="px-4 py-4">
      <h3 className="mb-1.5 font-mono text-[10px] uppercase tracking-wide text-ink-3">
        Tests &amp; procedures
      </h3>
      {query.isPending ? (
        <p className="text-[13px] text-ink-3" role="status">
          Loading prices…
        </p>
      ) : query.isError ? (
        <p className="text-[13px] text-ink-3" role="alert">
          Couldn&apos;t load prices.
        </p>
      ) : groups.length === 0 ? (
        <p className="text-[13px] text-ink-3">
          No procedures priced for this hospital yet.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => (
            <div key={`${g.kind}:${g.category_slug ?? g.heading}`}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                {g.heading}
              </div>
              <ul className="mt-1 divide-y divide-line-1">
                {g.procedures.map((p) => (
                  <li key={p.slug} className="flex items-center gap-2.5 py-2">
                    <span className="flex-1 text-[13px] text-ink-1">
                      {p.name}
                    </span>
                    {p.turnaround && (
                      <span className="text-[11px] text-ink-3">
                        {p.turnaround}
                      </span>
                    )}
                    {p.referral_required && (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink">
                        <Icon name="info" size={10} /> Referral
                      </span>
                    )}
                    {(() => {
                      // Same three states as the diagnostics screen, from the
                      // same helper — a second em dash here would have been a
                      // second place to get it wrong (ADR-0052).
                      const reading = readPrice(p.price_pesewas, {
                        varies: p.price_varies,
                        isMinimum: p.price_is_minimum,
                      })
                      return reading.kind === 'amount' ||
                        reading.kind === 'minimum' ? (
                        <span className="shrink-0 tabular-nums text-[13px] font-medium text-ink-1">
                          {reading.text}
                        </span>
                      ) : (
                        <span className="shrink-0 text-[11px] leading-tight text-ink-3">
                          {reading.text}
                        </span>
                      )
                    })()}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {/* The general statement, once, directly under the prices it qualifies
          (ADR-0054). Above the other notes because it is about the figures the
          patient has just read, not about where to find other ones. */}
      {groups.length > 0 && <PriceCaveat className="mt-3" />}

      <div className="mt-4 rounded-r3 border border-dashed border-line-2 p-3 text-[12px] text-ink-2">
        <p>
          <span className="font-semibold">Bed prices</span> show when you pick a
          bed type on the Beds tab.
        </p>
        <p className="mt-1.5">
          <span className="font-semibold">Need a medicine?</span>{' '}
          <Link
            to="/medicines"
            className="inline-flex items-center gap-1 font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Request it <Icon name="arrow" size={12} />
          </Link>
        </p>
      </div>
    </div>
  )
}
